import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { CarrierVisitDirection, ContainerHeight, ContainerType } from '@yard/app/api'
import { BooleanAutoComplete } from '@yard/components/AutoCompletes/BooleanAutoComplete/BooleanAutoComplete'
import { ContainerHeightAutoComplete } from '@yard/components/AutoCompletes/ContainerHeightAutoComplete/ContainerHeightAutoComplete'
import {
  ContainerLength,
  ContainerLengthAutoComplete,
} from '@yard/components/AutoCompletes/ContainerLengthAutoComplete/ContainerLengthAutoComplete'
import { ContainerTypeAutoComplete } from '@yard/components/AutoCompletes/ContainerTypeAutoComplete/ContainerTypeAutoComplete'
import { observer } from 'mobx-react-lite'
import { ReactNode } from 'react'
import { YardViewStore } from '../../stores/yard.view-store'
interface Props {
  viewStore: YardViewStore
}

export const YardViewFilterForm = observer((props: Props) => {
  const { viewStore } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  return (
    <Stack direction='column'>
      <FilterAccordion title={t('carrierType', 'Carrier Type')}>
        <Stack>
          {viewStore.transportSelection?.({
            direction: CarrierVisitDirection.Inbound,
            carrierType: viewStore.filters.inboundCarrierType,
            carrierVisitId: viewStore.filters.inboundCarrierVisitId,
            onChangeCarrierType: (value: any) =>
              viewStore.setFilters({ inboundCarrierType: value, inboundCarrierVisitId: undefined }),
            onChangeCarrierVisit: (value: any) =>
              viewStore.setFilters({ inboundCarrierVisitId: value }),
          })}
        </Stack>
        <Stack>
          {viewStore.transportSelection?.({
            direction: CarrierVisitDirection.Outbound,
            carrierType: viewStore.filters.outboundCarrierType,
            carrierVisitId: viewStore.filters.outboundCarrierVisitId,
            onChangeCarrierType: (value: any) =>
              viewStore.setFilters({
                outboundCarrierType: value,
                outboundCarrierVisitId: undefined,
              }),
            onChangeCarrierVisit: (value: any) =>
              viewStore.setFilters({ outboundCarrierVisitId: value }),
          })}
        </Stack>
      </FilterAccordion>

      <FilterAccordion title={t('generalAttributes', 'General Attributes')}>
        <Stack>
          <TextField
            value={viewStore.filters.containerNumber || ''}
            onChange={v => {
              const value = v.target.value

              if (value.trim() === '') {
                viewStore.setFilters({ containerNumber: undefined })
              } else {
                viewStore.setFilters({ containerNumber: value })
              }
            }}
            label={t('containerNumber', 'Container number')}
          />
        </Stack>
        <Stack>
          <ContainerHeightAutoComplete
            multiple
            defaultValue={viewStore.filters.containerHeight}
            onChange={v => {
              const values = v as ContainerHeight[]
              viewStore.setFilters({ containerHeight: values })
            }}
          />
        </Stack>

        <Stack>
          <ContainerLengthAutoComplete
            multiple
            defaultValue={viewStore.filters.containerLength}
            onChange={v => {
              const values = v as ContainerLength[]
              viewStore.setFilters({ containerLength: values })
            }}
          />
        </Stack>

        <Stack>
          <ContainerTypeAutoComplete
            multiple
            defaultValue={viewStore.filters.containerType}
            onChange={v => {
              const values = v as ContainerType[]
              viewStore.setFilters({ containerType: values })
            }}
          />
        </Stack>
      </FilterAccordion>

      <FilterAccordion title={t('destinations', 'Destinations')}>
        {viewStore.locationCodeAutoComplete?.({
          label: t('portOfLoading', 'Port of Loading'),
          defaultValue: viewStore.filters.portOfLoading,
          onChange: (value: any) => viewStore.setFilters({ portOfLoading: value }),
        })}
        {viewStore.locationCodeAutoComplete?.({
          label: t('portOfDischarge', 'Port of Discharge'),
          defaultValue: viewStore.filters.portOfDischarge,
          onChange: (value: any) => viewStore.setFilters({ portOfDischarge: value }),
        })}
      </FilterAccordion>

      <FilterAccordion title={t('containerOperator', 'Container Operator')}>
        {viewStore.companyAutoComplete?.({
          label: t('containerOperator', 'Container operator'),
          selectedReferenceId: viewStore.filters.containerOperatorId,
          handleOnChange: (value: any) =>
            viewStore.setFilters({ containerOperatorId: value?.referenceId }),
        })}
      </FilterAccordion>

      <FilterAccordion title={t('handlingInstructions', 'Handling Instructions')}>
        <Stack>
          <BooleanAutoComplete
            label={t('state', 'State')}
            entityDisplayTrue={t('empty', 'Empty')}
            entityDisplayFalse={t('full', 'Full')}
            selectedValue={viewStore.filters.isEmpty}
            onChange={v => {
              viewStore.setFilters({ isEmpty: v })
            }}
          />
        </Stack>

        <Stack>
          <BooleanAutoComplete
            label={t('dangerous', 'Dangerous')}
            entityDisplayTrue={t('dangerous', 'Dangerous')}
            entityDisplayFalse={t('notDangerous', 'Not dangerous')}
            selectedValue={viewStore.filters.isDangerous}
            onChange={v => {
              viewStore.setFilters({ isDangerous: v })
            }}
          />
        </Stack>

        <Stack>
          <BooleanAutoComplete
            label={t('reefer', 'Reefer')}
            entityDisplayTrue={t('reefer', 'Reefer')}
            entityDisplayFalse={t('notReefer', 'Not reefer')}
            selectedValue={viewStore.filters.isReefer}
            onChange={v => {
              viewStore.setFilters({ isReefer: v })
            }}
          />
        </Stack>

        <Stack>
          <BooleanAutoComplete
            label={t('oog', 'OOG')}
            entityDisplayTrue={t('oog', 'OOG')}
            entityDisplayFalse={t('notOog', 'Not OOG')}
            selectedValue={viewStore.filters.isOOG}
            onChange={v => {
              viewStore.setFilters({ isOOG: v })
            }}
          />
        </Stack>

        <Stack>
          <BooleanAutoComplete
            label={t('damaged', 'Damaged')}
            entityDisplayTrue={t('damaged', 'Damaged')}
            entityDisplayFalse={t('notDamaged', 'Not damaged')}
            selectedValue={viewStore.filters.isDamaged}
            onChange={v => {
              viewStore.setFilters({ isDamaged: v })
            }}
          />
        </Stack>
      </FilterAccordion>
    </Stack>
  )
})

const FilterAccordion = observer((props: { title: string; children: ReactNode }) => {
  const { title, children } = props
  const theme = useMinimalsTheme()

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { boxShadow: 'none' },
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: theme.customSpacing.s,
      }}
    >
      <AccordionSummary sx={{ m: 0, p: 0 }} expandIcon={<ExpandMore />}>
        <Typography variant='subtitle2'>{title}</Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ m: 0, p: 0, pb: theme.customSpacing.s }}>
        <Stack direction='column' gap={theme.customSpacing.s}>
          {children}
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
})
